import {
  CalendarDays,
  CheckCircle,
  ThumbsUp,
  ThumbsDown,
  Minus,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";
import { Badge } from "#app/components/ui/badge";
import { Button } from "#app/components/ui/button";
import { Card, CardContent } from "#app/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "#app/components/ui/dialog";
import { ScrollArea } from "#app/components/ui/scroll-area";

interface LastInteractionProps {
  lastUserReviews: Array<{
    formattedDate: string;
    rating: string | number;
    comment: string | null;
    kilowatts?: number | null;
    vehicleName?: string | null;
  }>;
}

export default function LastInteraction({
  lastUserReviews,
}: LastInteractionProps) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const getSentimentIcon = (sentiment: string | number) => {
    switch (String(sentiment)) {
      case "1":
        return <ThumbsUp className="h-4 w-4 text-green-500" />;
      case "2":
        return <Minus className="h-4 w-4 text-yellow-500" />;
      default:
        return <ThumbsDown className="h-4 w-4 text-red-500" />;
    }
  };

  const getSentimentText = (sentiment: string | number) => {
    switch (String(sentiment)) {
      case "1":
        return "positiva";
      case "2":
        return "neutra";
      default:
        return "negativa";
    }
  };

  const getSentimentStyles = (sentiment: string | number) => {
    switch (String(sentiment)) {
      case "1":
        return "bg-green-100 text-green-700";
      case "2":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-red-100 text-red-700";
    }
  };

  const lastUserReview = lastUserReviews[0];

  return (
    <Card className="w-full border border-border shadow-md">
      <CardContent className="space-y-6 px-4 py-4 md:py-4 md:px-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Suas visitas</h2>
          <Button
            variant="ghost"
            size="sm"
            className="p-1"
            onClick={toggleExpand}
            aria-label={expanded ? "Recolher detalhes" : "Expandir detalhes"}
          >
            {expanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Always visible summary */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium">
              Última visita: {lastUserReview.formattedDate}
            </span>
          </div>

          <span
            className={`flex items-center justify-center gap-2 rounded-md font-semibold ${getSentimentStyles(lastUserReview.rating)} px-2 py-0.5 text-sm`}
          >
            {getSentimentText(lastUserReview.rating)}
            {getSentimentIcon(lastUserReview.rating)}
          </span>
        </div>

        {/* Expandable content */}
        {expanded && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">Avaliações</span>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <button className="inline-flex items-center rounded-full border border-transparent bg-secondary px-2.5 py-0.5 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                    {lastUserReviews.length}{" "}
                    {lastUserReviews.length === 1 ? "avaliação" : "avaliações"}
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Seu Histórico de Visitas</DialogTitle>
                  </DialogHeader>
                  <ScrollArea className="h-[60vh] w-full pr-4">
                    <div className="space-y-4">
                      {lastUserReviews.map((review, index) => (
                        <div
                          key={index}
                          className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700"
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">
                              {review.formattedDate}
                            </span>
                            <span
                              className={`flex items-center justify-center gap-2 rounded-md font-semibold ${getSentimentStyles(review.rating)} px-2 py-0.5 text-sm`}
                            >
                              {getSentimentText(review.rating)}
                              {getSentimentIcon(review.rating)}
                            </span>
                          </div>
                          {review.comment && (
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              {review.comment}
                            </p>
                          )}
                          {(review.kilowatts || review.vehicleName) && (
                            <div className="mt-3 flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400">
                              {review.kilowatts && (
                                <span className="font-semibold">
                                  {review.kilowatts} kW
                                </span>
                              )}
                              {review.kilowatts && review.vehicleName && (
                                <span className="font-medium">-</span>
                              )}
                              {review.vehicleName && (
                                <span className="font-medium">
                                  {review.vehicleName}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {(lastUserReview.comment ||
              lastUserReview.kilowatts ||
              lastUserReview.vehicleName) && (
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      Sua última avaliação foi
                    </span>
                  </div>
                </div>
                {lastUserReview.comment && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    "{lastUserReview.comment}"
                  </p>
                )}
                {(lastUserReview.kilowatts || lastUserReview.vehicleName) && (
                  <div className="mt-3 flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400">
                    {lastUserReview.kilowatts && (
                      <span className="font-semibold">
                        {lastUserReview.kilowatts} kW
                      </span>
                    )}
                    {lastUserReview.kilowatts && lastUserReview.vehicleName && (
                      <span className="font-medium">-</span>
                    )}
                    {lastUserReview.vehicleName && (
                      <span className="font-medium">
                        {lastUserReview.vehicleName}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
